import Phaser from 'phaser';
import { DominoTile } from './DominoTile';
import {
  engineToScreen,
  getBoardUnitSize,
} from '../utils/coordinates';
import { COLORS } from '../config/theme';
import { drawnTableSize } from '../utils/sceneLayout';
import type { PositionedTile, BoardLayout, ValidPosition, HandTile } from '../types/game';
import { GAME_DIAGNOSTICS, diag } from '../debug/gameDiagnostics';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// changes). Skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import type { ViewerTileSkin, ViewerTableSkin } from '../config/viewerCosmetics';
import { getViewerPlacedTextureKey } from '../config/viewerTileSkinAssets';
import { getViewerTableTextureKey } from '../config/viewerTableSkinAssets';

/** State of one in-flight authoritative travel ghost — registered so a later
 *  autoFit/resize can retarget the flight instead of letting it land at a
 *  stale destination (the source of the misplacement flicker). */
interface AuthoritativeTravel {
  tileId:    number;
  tile:      DominoTile;
  ghost:     DominoTile;
  tween:     Phaser.Tweens.Tween | null;
  destX:     number;
  destY:     number;
  /** Flight launch timestamp — remaining time stays correct across repeated retargets. */
  launchedAt: number;
  /** Touchdown handler (reveal real tile, destroy ghost, settle). Shared by
   *  the original flight tween and any retarget tween. */
  complete:  () => void;
}

/** State of one in-progress optimistic travel (desktop and mobile). */
interface OptimisticTravel {
  tileId:       number;
  ghost:        DominoTile;
  tween:        Phaser.Tweens.Tween | null;
  startAngle:   number;
  landed:       boolean;
  adoptedTile:  DominoTile | null;
  // Authoritative engine coordinates from ValidPosition — the fixed truth the
  // scene-space destination is re-derived from whenever the board transform
  // changes (mobile autoFit, resize).
  engineX:      number;
  engineY:      number;
  // Scene-space destination the current tween is aimed at.
  destX:        number;
  destY:        number;
  onSuperseded?: () => void;
}


/**
 * Board is a Phaser Container that represents the game table.
 * It renders all placed tiles and supports pan/zoom.
 * Styled as a warm wood table with gold border frame.
 */
export class Board extends Phaser.GameObjects.Container {
  private tileSprites: Map<number, DominoTile> = new Map();
  private feltBg!: Phaser.GameObjects.Graphics;
  private feltImages: Phaser.GameObjects.Image[] = [];
  private mobileMode = false;
  private placementAnimationsEnabled = true;
  private _onTileSettle: (() => void) | null = null;
  private _dragHighlightGfx: Phaser.GameObjects.Graphics | null = null;

  // Active travel animation references — stored for safe snap on resize.
  private _activeTravelTween:  Phaser.Tweens.Tween | null = null;
  private _activeTravelGhost:  DominoTile | null = null;
  private _activeTravelTileId: number | null = null;

  // Animations deferred until after autoFitToPlayArea() so destX/destY use the final board transform.
  private _pendingTravelAnimations: Array<{
    tile:       DominoTile;
    fromPos:    { x: number; y: number };
    startAngle: number;
    onSettle?:  () => void;
  }> = [];

  // Desktop-only optimistic travel: a visual-only ghost launched at pointer
  // release toward the server-precomputed valid_positions destination, before
  // the REST/WS round trip completes. Never mutates board state — the
  // authoritative tile is adopted (hidden, then revealed at touchdown) when
  // the server update renders it.
  /** Total flight time of a travel ghost — authoritative and optimistic. */
  private static readonly TRAVEL_MS = 500;
  /** Destination changes at or below this distance (px) are not worth a visible correction. */
  private static readonly RETARGET_EPS = 0.75;

  private _optimistic: OptimisticTravel | null = null;

  // In-flight authoritative travel ghosts, keyed by tileId. Retargeted by
  // reconcileOptimisticTravel() when autoFit/resize moves the board transform.
  private _authTravels: Map<number, AuthoritativeTravel> = new Map();

  // Click/tap destination previews: semi-transparent dominoes rendered at the
  // exact final placements from valid_positions. Container children, so they
  // pan/zoom/fit with the board. Hit-testing goes through
  // hitTestPlacementPreview (Hand's tap router) rather than per-object input.
  private _placementPreviews: Array<{ vp: ValidPosition; tile: DominoTile }> = [];
  // Drag-intent visual state for the previews: which target is active.
  // null = click-selection idle mode (hover effects allowed).
  private _previewActiveState: 'left' | 'right' | 'rack' | null = null;

  // Board center in screen coordinates (within this container's local space)
  private boardCenterX = 0;
  private boardCenterY = 0;

  // Pan/zoom state
  private isPanReady = false;  // pointer is down, waiting for drag threshold
  private isPanning = false;   // actively panning (threshold met)
  private pointerStartX = 0;
  private pointerStartY = 0;
  private dragStartX = 0;
  private dragStartY = 0;
  private currentZoom = 1;
  private minZoom = 0.4;
  private maxZoom = 2.5;
  private userIsPanning = false;
  private panTimeout: ReturnType<typeof setTimeout> | null = null;
  private static readonly DRAG_THRESHOLD = 8; // pixels before pan starts

  // Cosmetics: viewer-selected skins (durable). Selection source is the
  // session's effective per-viewer cosmetics (resolved in GameScreen).
  private viewerTileSkin: ViewerTileSkin;
  private viewerTableSkin: ViewerTableSkin;

  constructor(scene: Phaser.Scene, mobileMode = false, viewerTileSkin: ViewerTileSkin = 'classic', viewerTableSkin: ViewerTableSkin = 'green_fabric') {
    super(scene, 0, 0);

    this.mobileMode = mobileMode;
    this.viewerTileSkin = viewerTileSkin;
    this.viewerTableSkin = viewerTableSkin;
    this.boardCenterX = 0;
    this.boardCenterY = 0;

    this.drawFelt();

    scene.add.existing(this);
  }

  /** Switch between mobile (no frame) and desktop (wood table) rendering */
  setMobileMode(mobile: boolean): void {
    this.mobileMode = mobile;
  }

  /** Enable or disable tile placement animations (game-feel feature, on by default). */
  setPlacementAnimationsEnabled(enabled: boolean): void {
    this.placementAnimationsEnabled = enabled;
  }

  /** Register a callback that fires once each time a newly placed tile finishes settling. */
  setOnTileSettle(cb: (() => void) | null): void {
    this._onTileSettle = cb;
  }

  /**
   * Redraw the felt surface using the current global unit size.
   * Call after GameScene updates the unit via setBoardUnitSize().
   */
  fitToZone(): void {
    this.remove(this.feltBg, false);
    this.feltBg.destroy();
    for (const img of this.feltImages) {
      this.remove(img, false);
      img.destroy();
    }
    this.feltImages = [];
    this.drawFelt();
  }

  /** Draw the board background — clean dark surface on mobile, wood table on desktop */
  private drawFelt(): void {
    const unit      = getBoardUnitSize();
    // Desktop: the drawn table fills the canvas square edge-to-edge, via the
    // SAME shared function the scene layout uses (drawnTableSize — see the
    // CRITICAL INVARIANT in utils/sceneLayout.ts), so the drawn image and the
    // scene's believed table bounds can never drift apart. Tile positions and
    // sizes are unchanged — only the background artwork absorbs the sub-unit
    // remainder instead of leaving a visible backdrop strip.
    const tableSize = this.mobileMode
      ? unit * 19
      : drawnTableSize(this.scene.scale.width, this.scene.scale.height);
    const half      = tableSize / 2;

    this.feltImages = [];

    // ── Mobile: no board background — GameScene provides a full-screen felt ──
    if (this.mobileMode) {
      // Empty placeholder so feltBg reference stays valid; background is
      // handled by the fullscreen felt image in GameScene.
      this.feltBg = this.scene.add.graphics();
      this.addAt(this.feltBg, 0);
      return;
    }

    // Cosmetics: bind the viewer's selected table skin if its texture loaded;
    // otherwise fall back to the classic 'desktop-table'. This ONLY swaps the
    // background image — tableSize and all board geometry are unchanged.
    const tableSkinKey    = getViewerTableTextureKey(this.viewerTableSkin);
    const desktopTableKey = this.scene.textures.exists(tableSkinKey) ? tableSkinKey : 'desktop-table';

    if (!this.mobileMode && this.scene.textures.exists(desktopTableKey)) {
      // ── Desktop single-image path ─────────────────────────
      // A self-contained square PNG (wood frame + felt surface). Rendered as-is
      // at the target tableSize; no secondary felt layer needed.
      const tableImg = this.scene.add.image(0, 0, desktopTableKey);
      tableImg.setDisplaySize(tableSize, tableSize);
      this.addAt(tableImg, 0);
      this.feltImages.push(tableImg);

      this.feltBg = this.scene.add.graphics();
      this.addAt(this.feltBg, 1);

    } else if (this.scene.textures.exists('table') && this.scene.textures.exists('felt')) {
      // ── PNG fallback: separate table frame + felt layer ───
      const tableSrc  = this.scene.textures.get('table').getSourceImage() as HTMLImageElement;
      const feltSrc   = this.scene.textures.get('felt').getSourceImage() as HTMLImageElement;

      const scaleX = tableSize / tableSrc.width;
      const scaleY = tableSize / tableSrc.height;

      const feltW  = Math.round(feltSrc.width  * scaleX);
      const feltH  = Math.round(feltSrc.height * scaleY);

      const feltImg = this.scene.add.image(0, 0, 'felt');
      feltImg.setDisplaySize(feltW, feltH);
      this.addAt(feltImg, 0);
      this.feltImages.push(feltImg);

      const tableImg = this.scene.add.image(0, 0, 'table');
      tableImg.setDisplaySize(tableSize, tableSize);
      this.addAt(tableImg, 1);
      this.feltImages.push(tableImg);

      this.feltBg = this.scene.add.graphics();
      this.addAt(this.feltBg, 2);

    } else {
      // ── Graphics fallback (no assets) ────────────────────
      const half = tableSize / 2;
      const gfx  = this.scene.add.graphics();

      gfx.fillStyle(COLORS.bgFeltFrame, 1);
      gfx.fillRoundedRect(-half - 8, -half - 8, tableSize + 16, tableSize + 16, 12);
      gfx.lineStyle(2, COLORS.gold, 0.6);
      gfx.strokeRoundedRect(-half - 8, -half - 8, tableSize + 16, tableSize + 16, 12);
      gfx.fillStyle(COLORS.bgFelt, 1);
      gfx.fillRoundedRect(-half, -half, tableSize, tableSize, 6);

      this.addAt(gfx, 0);
      this.feltBg = gfx;
    }
  }

  /** Enable pan (drag) and zoom (scroll wheel / pinch) */
  private setupPanZoom(): void {
    // Make the entire scene interactive for dragging
    const hitArea = new Phaser.Geom.Rectangle(
      -2000, -2000, 4000, 4000
    );
    this.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    this.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Don't start dragging immediately — wait for threshold
      this.isPanReady = true;
      this.isPanning = false;
      this.pointerStartX = pointer.x;
      this.pointerStartY = pointer.y;
      this.dragStartX = pointer.x - this.x;
      this.dragStartY = pointer.y - this.y;

      if (this.panTimeout) clearTimeout(this.panTimeout);
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.isPanReady || !pointer.isDown) return;

      if (!this.isPanning) {
        // Check if we've moved past the drag threshold
        const dx = pointer.x - this.pointerStartX;
        const dy = pointer.y - this.pointerStartY;
        if (Math.abs(dx) > Board.DRAG_THRESHOLD || Math.abs(dy) > Board.DRAG_THRESHOLD) {
          this.isPanning = true;
          this.userIsPanning = true;
        }
      }

      if (this.isPanning) {
        this.x = pointer.x - this.dragStartX;
        this.y = pointer.y - this.dragStartY;
      }
    });

    this.scene.input.on('pointerup', () => {
      this.isPanReady = false;
      this.isPanning = false;
      // After 3s of no panning, allow auto-scroll again
      if (this.panTimeout) clearTimeout(this.panTimeout);
      this.panTimeout = setTimeout(() => {
        this.userIsPanning = false;
      }, 3000);
    });

    // Zoom with mouse wheel
    this.scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number) => {
      const zoomDelta = deltaY > 0 ? -0.1 : 0.1;
      this.currentZoom = Phaser.Math.Clamp(
        this.currentZoom + zoomDelta,
        this.minZoom,
        this.maxZoom
      );
      this.setScale(this.currentZoom);
    });
  }

  // ─── Public API ──────────────────────────────────────────

  /**
   * Diff-render: keep existing tile sprites, add and animate only new ones.
   * travelSources: optional map of tileId → scene-space origin for the
   * hand-to-table travel animation. Omit on resize re-renders.
   */
  renderBoard(
    tiles: PositionedTile[],
    travelSources?: Map<number, { x: number; y: number }>,
  ): void {
    if (!this.scene) {
      console.warn('[Board] renderBoard called on destroyed Board — ignored');
      return;
    }

    const isFirstRender = this.tileSprites.size === 0;
    const incomingIds   = new Set(tiles.map(t => t.id));

    // Any board change invalidates click destination previews — the ends they
    // were computed for may no longer exist (timer auto-play, new round).
    if (this._placementPreviews.length > 0 &&
        (tiles.length < this.tileSprites.size || tiles.some(t => !this.tileSprites.has(t.id)))) {
      this.clearPlacementPreviews();
    }

    // Timer-race / supersede check: the board advanced (a tile was added, or a
    // new round reset it) WITHOUT our optimistic tile — the server placed a
    // different move (e.g. turn-timeout auto-play). Cancel the flight; the
    // ghost must never land as though accepted. A duplicate update carrying an
    // unchanged board keeps the flight alive (our confirmation is still coming,
    // or the REST rejection path will cancel it).
    if (this._optimistic && !incomingIds.has(this._optimistic.tileId)) {
      const boardChanged =
        tiles.length < this.tileSprites.size ||
        tiles.some(t => !this.tileSprites.has(t.id));
      if (boardChanged) {
        const cb = this._optimistic.onSuperseded;
        if (cb) cb(); else this.cancelOptimisticTravel();
        // Safety net if the callback did not cancel.
        if (this._optimistic && !incomingIds.has(this._optimistic.tileId)) {
          this.cancelOptimisticTravel();
        }
      }
    }

    // Remove any stale sprites that are no longer in the layout (safety guard —
    // domino tiles never leave the board, but reconnect edge cases may differ).
    for (const [id, tile] of this.tileSprites) {
      if (!incomingIds.has(id)) {
        tile.destroy();
        this.tileSprites.delete(id);
      }
    }

    // Add only tiles not already rendered.  On first render suppress animation
    // so a full initial board (mid-session join or reconnect) doesn't burst-animate
    // every tile at once.
    for (const tileData of tiles) {
      if (!this.tileSprites.has(tileData.id)) {
        const from = isFirstRender ? undefined : travelSources?.get(tileData.id);
        this.addTileFromData(tileData, !isFirstRender, from);
      }
    }
  }

  /** Add a single tile to the board */
  addTile(tileData: PositionedTile, animate: boolean): void {
    this.addTileFromData(tileData, animate);
  }

  /** Create a DominoTile from positioned tile data */
  private addTileFromData(
    tileData: PositionedTile,
    animate: boolean,
    fromPos?: { x: number; y: number },
  ): void {
    if (!this.scene) {
      console.warn('[Board] addTileFromData called on destroyed Board — ignored');
      return;
    }
    const rawPos = engineToScreen(
      tileData.position.x,
      tileData.position.y,
      this.boardCenterX,
      this.boardCenterY
    );
    // Desktop only: snap each placed tile to a whole pixel. Engine positions
    // are multiples of 0.5 and the board unit can be an odd number, so the
    // raw screen coordinate often lands on a sub-pixel boundary — that's what
    // was making tile PNGs look slightly blurry. Mobile keeps the raw
    // fractional position because its board container is later rescaled by
    // autoFitToPlayArea anyway, so pre-rounding here wouldn't help there.
    const pos = this.mobileMode ? rawPos : { x: Math.round(rawPos.x), y: Math.round(rawPos.y) };

    // Determine the correct rendering order of values using engine_data.
    // The engine provides near_val (value facing the connection/seam) and
    // far_val (value facing away). ndir is the growth direction of the chain.
    //
    // For horizontal tiles: left half = side A, right half = side B
    //   ndir "right" → near side is LEFT  → leftVal=near_val, rightVal=far_val
    //   ndir "left"  → near side is RIGHT → leftVal=far_val, rightVal=near_val
    //
    // For vertical tiles: top half = side A, bottom half = side B
    // NOTE: Engine uses Unity Y-Up convention for ndir (+Y=up), but screen Y goes down.
    // So engine "down" (neg Y) = visually UP on screen, engine "up" (pos Y) = visually DOWN.
    //   ndir "down" → screen UP   → near side is BOTTOM → topVal=far_val, bottomVal=near_val
    //   ndir "up"   → screen DOWN → near side is TOP    → topVal=near_val, bottomVal=far_val
    const ed = tileData.engine_data;
    const { renderA, renderB } = this._resolveRenderValues(
      tileData.value_a, tileData.value_b, tileData.position.orientation, ed);
    if (ed && ed.near_val != null && ed.far_val != null && ed.ndir) {
      console.log(`[Board] Tile ${tileData.id} [${tileData.value_a}|${tileData.value_b}] → [${renderA}|${renderB}] (${tileData.position.orientation === 'horizontal' ? 'H' : 'V'}, ndir=${ed.ndir})`);
    }

    const tile = new DominoTile(
      this.scene,
      pos.x,
      pos.y,
      tileData.id,
      renderA,
      renderB,
      tileData.position.orientation,
      1,
      this.viewerTileSkin !== 'classic' ? getViewerPlacedTextureKey(this.viewerTileSkin, renderA, renderB) : undefined
    );

    // Remove from scene's display list and add to this container instead
    this.scene.children.remove(tile);
    this.add(tile);
    this.tileSprites.set(tileData.id, tile);

    if (GAME_DIAGNOSTICS) {
      diag.registerTileEngineCoords(tileData.id, tileData.position.x, tileData.position.y);
      diag.recordTilePlacement({
        txnId:               diag.currentTxnId,
        tileId:              tileData.id,
        valueA:              renderA,
        valueB:              renderB,
        engineX:             tileData.position.x,
        engineY:             tileData.position.y,
        expectedLocalX:      rawPos.x,
        expectedLocalY:      rawPos.y,
        actualLocalX:        pos.x,
        actualLocalY:        pos.y,
        boardXAtCreation:    this.x,
        boardYAtCreation:    this.y,
        boardScaleAtCreation: this.scaleX,
        boardUnitAtCreation: getBoardUnitSize(),
        angle:               tile.angle,
        scaleX:              tile.scaleX,
        scaleY:              tile.scaleY,
        alpha:               tile.alpha,
        depth:               tile.depth,
        misplacement:        false,
      });
    }

    // Optimistic tile confirmed: adopt the authoritative sprite instead of
    // queuing a duplicate travel animation. Applies ONLY to the exact
    // optimistic tileId — opponent tiles and future moves are unaffected.
    // Checked before the animate gate so a no-animation render (reconnect
    // first render) still resolves the in-flight ghost instead of showing
    // both objects.
    if (this._optimistic && this._optimistic.tileId === tileData.id) {
      this._adoptOptimisticTile(tile);
      return;
    }

    if (animate && this.placementAnimationsEnabled) {
      if (fromPos) {
        // Start angle that makes the board-oriented ghost visually match the
        // hand tile (vertical, canonical value_a on top), so the flight begins
        // with no snap and the tile rotates naturally toward angle 0 in-flight.
        // Phaser +angle = clockwise. Horizontal +90° puts renderA on top.
        const startAngle = this._travelStartAngle(renderA, tileData.value_a, tileData.position.orientation);
        this._pendingTravelAnimations.push({
          tile,
          fromPos,
          startAngle,
          onSettle: this._onTileSettle ?? undefined,
        });
      } else {
        tile.animatePlace(this._onTileSettle ?? undefined);
      }
    }
  }

  /**
   * Pip rendering order for a placed tile: which value shows on the A half
   * (left/top) vs the B half. Derived from engine near/far values and the
   * chain growth direction. Shared by authoritative rendering and the
   * optimistic travel ghost so both always agree.
   */
  private _resolveRenderValues(
    valueA: number,
    valueB: number,
    orientation: 'horizontal' | 'vertical',
    ed?: { near_val?: number | null; far_val?: number | null; ndir?: string | null } | null,
  ): { renderA: number; renderB: number } {
    let renderA = valueA;
    let renderB = valueB;
    if (ed && ed.near_val != null && ed.far_val != null && ed.ndir) {
      if (orientation === 'horizontal') {
        // Horizontal: A=left, B=right
        if (ed.ndir === 'right') {
          // Chain grows right → near (connecting) side is left
          renderA = ed.near_val;
          renderB = ed.far_val;
        } else if (ed.ndir === 'left') {
          // Chain grows left → near (connecting) side is right
          renderA = ed.far_val;
          renderB = ed.near_val;
        }
      } else {
        // Vertical: A=top (on screen), B=bottom (on screen)
        // Engine ndir is inverted for Y axis (Unity Y-up vs screen Y-down)
        if (ed.ndir === 'down') {
          // Engine "down" = screen UP → tile grew upward → near side is at BOTTOM on screen
          renderA = ed.far_val;   // top on screen = far from seam
          renderB = ed.near_val;  // bottom on screen = near to seam
        } else if (ed.ndir === 'up') {
          // Engine "up" = screen DOWN → tile grew downward → near side is at TOP on screen
          renderA = ed.near_val;  // top on screen = near to seam
          renderB = ed.far_val;   // bottom on screen = far from seam
        }
      }
    }
    return { renderA, renderB };
  }

  /** Start angle that makes a board-oriented travel ghost visually match the
   *  hand tile (vertical, canonical value_a on top) at launch. */
  private _travelStartAngle(
    renderA: number,
    canonicalValueA: number,
    orientation: 'horizontal' | 'vertical',
  ): number {
    const aIsCanonicalTop = renderA === canonicalValueA;
    return orientation === 'horizontal'
      ? (aIsCanonicalTop ? 90 : -90)
      : (aIsCanonicalTop ? 0 : 180);
  }

  /**
   * Ghost-tile travel animation: a clone slides from fromPos (scene coords)
   * to the board tile's world position, then fades; the real tile pops in
   * with animatePlace() as the ghost disappears.
   */
  private _runTravelAnimation(
    tile:       DominoTile,
    fromPos:    { x: number; y: number },
    startAngle: number,
    onSettle?:  () => void,
  ): void {
    if (!this.scene?.tweens) { tile.animatePlace(onSettle); return; }

    // World position of the real tile inside this (possibly zoomed) container.
    const destX = this.x + tile.x * this.scaleX;
    const destY = this.y + tile.y * this.scaleY;

    // Capture diagnostic context before autoFitToPlayArea can change board transform.
    const _diagTxnId = GAME_DIAGNOSTICS ? diag.currentTxnId : -1;
    if (GAME_DIAGNOSTICS) {
      diag.notifyTravelStart(_diagTxnId, tile.tileId, destX, destY, {
        x: this.x, y: this.y, scaleX: this.scaleX, scaleY: this.scaleY,
      });
    }

    // Hide real tile while ghost is in flight — revealed at touchdown.
    tile.setAlpha(0);

    // Ghost: same appearance as the final board tile, lives directly in the scene.
    const ghost = new DominoTile(
      this.scene,
      fromPos.x,
      fromPos.y,
      tile.tileId,
      tile.valueA,
      tile.valueB,
      tile.orientation,
      1,
      this.viewerTileSkin !== 'classic' ? getViewerPlacedTextureKey(this.viewerTileSkin, tile.valueA, tile.valueB) : undefined
    );
    ghost.setDepth(200);
    // Start at rest scale — the ghost takes over from the held drag ghost (or
    // rack tile) at full size, so the handoff has no shrink pop.
    ghost.setScale(1);
    // Begin visually matching the hand tile's orientation/pip order; the
    // travel tween rotates it to the board's final angle (0) in-flight, so
    // the pip flip reads as a physical rotation instead of a snap.
    ghost.setAngle(startAngle);

    // Registered so a later autoFit/resize can retarget this flight via
    // _retargetAuthoritativeTravels() instead of letting it land stale.
    const entry: AuthoritativeTravel = {
      tileId:     tile.tileId,
      tile,
      ghost,
      tween:      null,
      destX,
      destY,
      launchedAt: performance.now(),
      complete:   () => {},
    };
    entry.complete = () => {
      entry.tween = null;
      this._authTravels.delete(entry.tileId);
      // Clear legacy refs — normal completion path.
      if (this._activeTravelTileId === tile.tileId) {
        this._activeTravelTween  = null;
        this._activeTravelGhost  = null;
        this._activeTravelTileId = null;
      }
      // Scene may have shut down during the 500ms travel — animatePlace
      // guards itself too, but avoid the setAlpha call on a dead object.
      if (!tile.active || !tile.scene?.tweens) { ghost.destroy(); onSettle?.(); return; }
      tile.setAlpha(1);          // real tile visible first…
      ghost.destroy();           // …then the ghost goes, same tick — no gap
      if (GAME_DIAGNOSTICS) {
        const finalWorldX   = this.x + tile.x * this.scaleX;
        const finalWorldY   = this.y + tile.y * this.scaleY;
        const activeTweens  = tile.scene.tweens.getTweensOf(tile).length;
        diag.notifyTravelEnd(_diagTxnId, tile.tileId, finalWorldX, finalWorldY, activeTweens);
      }
      tile.animatePlace(onSettle);
    };

    // Travel to board position — ghost stays fully solid for the whole flight.
    // It arrives at scale 1.06, the exact starting scale of animatePlace, so
    // the hard swap at touchdown is pixel-identical: reveal the real tile
    // first, destroy the ghost in the same tick, then run the press settle.
    // No fades during flight — crossfading a moving ghost into a stationary
    // tile made the eye see two separate objects at the last moment.
    const tweenConfig: Phaser.Types.Tweens.TweenBuilderConfig = {
      targets:  ghost,
      x:        destX,
      y:        destY,
      scaleX:   1.06,
      scaleY:   1.06,
      angle:    0,
      duration: Board.TRAVEL_MS,
      ease:     'Power2.easeOut',
      onComplete: entry.complete,
    };
    // Capture the first onUpdate tick — the first Phaser game-loop frame where
    // ghost.x has moved.  This is the "first visible movement" timestamp.
    // _diagGen is filled in after tweens.add(); the closure captures it by
    // variable-reference, so recordTravelFirstUpdate sees the correct value
    // on the next frame (JS closures close over the binding, not the value).
    let tween: Phaser.Tweens.Tween;
    if (GAME_DIAGNOSTICS) {
      let _ptFirstUpdate = false;
      let _diagGen = -1;
      tweenConfig.onUpdate = () => {
        if (!_ptFirstUpdate) { _ptFirstUpdate = true; diag.recordTravelFirstUpdate(_diagGen); }
      };
      tween = this.scene.tweens.add(tweenConfig);
      _diagGen = diag.recordTravelTweenCreated();
    } else {
      tween = this.scene.tweens.add(tweenConfig);
    }
    // Store refs so a resize can safely snap this in-flight tween.
    this._activeTravelTween  = tween;
    this._activeTravelGhost  = ghost;
    this._activeTravelTileId = tile.tileId;
    entry.tween = tween;
    this._authTravels.set(entry.tileId, entry);
  }

  /**
   * Retarget every in-flight authoritative travel ghost after the board
   * transform changed (mobile autoFit, resize). The corrected destination is
   * re-derived from the real tile's container position — the same value the
   * touchdown swap uses — so the ghost always lands exactly where the tile
   * will be revealed. Mirrors the optimistic retarget behavior.
   */
  private _retargetAuthoritativeTravels(): void {
    if (!this.scene?.tweens || this._authTravels.size === 0) return;
    for (const entry of this._authTravels.values()) {
      if (!entry.tile.active || !entry.ghost.active || !entry.tween) continue;
      const newX = this.x + entry.tile.x * this.scaleX;
      const newY = this.y + entry.tile.y * this.scaleY;
      if (Math.hypot(newX - entry.destX, newY - entry.destY) <= Board.RETARGET_EPS) continue;

      entry.destX = newX;
      entry.destY = newY;
      // Continue from the current position using the remaining flight time
      // (measured from launch so repeated retargets don't stretch the trip),
      // floored so a nearly finished flight doesn't visibly teleport.
      const remaining = Math.max(140, Board.TRAVEL_MS - (performance.now() - entry.launchedAt));
      entry.tween.remove();
      entry.tween = this.scene.tweens.add({
        targets:  entry.ghost,
        x:        newX,
        y:        newY,
        scaleX:   1.06,
        scaleY:   1.06,
        angle:    0,
        duration: remaining,
        ease:     'Power2.easeOut',
        onComplete: entry.complete,
      });
    }
  }

  /**
   * Start all travel animations that were deferred during renderBoard().
   * Must be called after autoFitToPlayArea() so destX/destY use the final transform.
   */
  flushPendingTravelAnimations(): void {
    for (const p of this._pendingTravelAnimations) {
      this._runTravelAnimation(p.tile, p.fromPos, p.startAngle, p.onSettle);
    }
    this._pendingTravelAnimations = [];
  }

  // ─── Optimistic travel (desktop only) ────────────────────

  /**
   * Launch a visual-only ghost from fromPos toward the server-precomputed
   * valid_positions destination, in the same frame as pointer release.
   *
   * Purely cosmetic: no tile is inserted into the board, no state mutated.
   * The authoritative tile from the WS update is adopted in addTileFromData
   * (hidden, revealed at ghost touchdown). Caller must only use this when the
   * board transform is static (desktop — autoFitToPlayArea never runs), so
   * the destination computed here matches the final tile position exactly.
   *
   * onSuperseded fires if an authoritative update advances the board WITHOUT
   * this tile (turn-timeout auto-play race); the callback should roll the
   * ghost back via cancelOptimisticTravel.
   *
   * Returns false when the ghost could not be started (missing scene/tweens).
   */
  startOptimisticTravel(
    tileData: HandTile,
    vp: ValidPosition,
    fromPos: { x: number; y: number },
    onSuperseded?: () => void,
  ): boolean {
    if (!this.scene?.tweens) return false;
    // Interaction locking makes a second concurrent play impossible; if one
    // somehow survives, resolve it before starting a new flight.
    if (this._optimistic) this.cancelOptimisticTravel();

    const ed = vp.engine_data as {
      near_val?: number | null; far_val?: number | null; ndir?: string | null;
    } | undefined;
    const { renderA, renderB } = this._resolveRenderValues(
      tileData.value_a, tileData.value_b, vp.orientation, ed);

    // Same conversion chain as the authoritative tile: engineToScreen, then
    // the desktop whole-pixel snap, then the container transform — so the
    // ghost's touchdown point is pixel-identical to the real tile.
    const rawLocal = engineToScreen(vp.x, vp.y, this.boardCenterX, this.boardCenterY);
    const local    = this.mobileMode ? rawLocal : { x: Math.round(rawLocal.x), y: Math.round(rawLocal.y) };
    const destX    = this.x + local.x * this.scaleX;
    const destY    = this.y + local.y * this.scaleY;

    const startAngle = this._travelStartAngle(renderA, tileData.value_a, vp.orientation);

    // Ghost mirrors _runTravelAnimation's: board-oriented, launched at the
    // hand-matching angle, rotating to 0 in flight, arriving at 1.06 (the
    // animatePlace starting scale) for a pixel-identical touchdown swap.
    const ghost = new DominoTile(
      this.scene, fromPos.x, fromPos.y,
      tileData.id, renderA, renderB, vp.orientation,
      1,
      this.viewerTileSkin !== 'classic' ? getViewerPlacedTextureKey(this.viewerTileSkin, renderA, renderB) : undefined
    );
    ghost.setDepth(200);
    ghost.setScale(1);
    ghost.setAngle(startAngle);

    const opt = {
      tileId:      tileData.id,
      ghost,
      tween:       null as Phaser.Tweens.Tween | null,
      startAngle,
      landed:      false,
      adoptedTile: null as DominoTile | null,
      engineX:     vp.x,
      engineY:     vp.y,
      destX,
      destY,
      onSuperseded,
    };
    opt.tween = this.scene.tweens.add({
      targets:  ghost,
      x:        destX,
      y:        destY,
      scaleX:   1.06,
      scaleY:   1.06,
      angle:    0,
      duration: Board.TRAVEL_MS,
      ease:     'Power2.easeOut',
      onComplete: () => this._onOptimisticFlightComplete(opt),
    });
    this._optimistic = opt;
    return true;
  }

  /** Shared arrival handler for the initial flight, mid-flight retargets, and
   *  post-landing corrections. Holds at destination until confirmation. */
  private _onOptimisticFlightComplete(opt: OptimisticTravel): void {
    opt.tween = null;
    if (this._optimistic !== opt) return;  // cancelled/superseded mid-flight
    opt.landed = true;
    // Server already confirmed → reveal now. Otherwise hold at destination
    // (scale 1.06) until confirmation; the animatePlace settle then doubles
    // as the confirmation beat.
    if (opt.adoptedTile) this._completeOptimisticTouchdown();
  }

  /**
   * Re-derive the ghost's scene-space destination from its authoritative
   * engine coordinates under the CURRENT board transform, and correct the
   * flight if it moved. Called after autoFitToPlayArea (mobile) and after
   * resize on both platforms; inert when the destination hasn't changed
   * beyond subpixel noise — which is always the case on an unresized desktop,
   * so validated desktop timing is untouched.
   */
  reconcileOptimisticTravel(): void {
    // Authoritative ghosts from earlier updates may still be in flight when a
    // new update refits the board — retarget them under the same call.
    this._retargetAuthoritativeTravels();

    const opt = this._optimistic;
    if (!opt || !this.scene?.tweens) return;

    // Same conversion chain as startOptimisticTravel / addTileFromData — on
    // both platforms this equals the authoritative tile's world position.
    const rawLocal = engineToScreen(opt.engineX, opt.engineY, this.boardCenterX, this.boardCenterY);
    const local    = this.mobileMode ? rawLocal : { x: Math.round(rawLocal.x), y: Math.round(rawLocal.y) };
    const newX     = this.x + local.x * this.scaleX;
    const newY     = this.y + local.y * this.scaleY;

    const moved = Math.hypot(newX - opt.destX, newY - opt.destY);
    const EPS   = Board.RETARGET_EPS;

    if (moved <= EPS) {
      // Destination unchanged: if the ghost is already parked and the server
      // has confirmed, finish the touchdown now (deferred from adoption so
      // the reveal always happens under the post-autoFit transform).
      if (opt.landed && !opt.tween && opt.adoptedTile) this._completeOptimisticTouchdown();
      return;
    }

    opt.destX = newX;
    opt.destY = newY;
    const ghost = opt.ghost;
    if (!ghost.active) return;

    if (opt.tween) {
      // In flight: continue from the current position toward the corrected
      // destination using the remaining travel time (floored so a nearly
      // finished flight doesn't visibly teleport).
      const remaining = Math.max(140, Board.TRAVEL_MS - opt.tween.elapsed);
      opt.tween.remove();
      opt.tween = this.scene.tweens.add({
        targets:  ghost,
        x:        newX,
        y:        newY,
        scaleX:   1.06,
        scaleY:   1.06,
        angle:    0,
        duration: remaining,
        ease:     'Power2.easeOut',
        onComplete: () => this._onOptimisticFlightComplete(opt),
      });
    } else if (opt.landed) {
      // Parked at the stale destination: short restrained slide to the
      // corrected spot, then the normal reveal path.
      opt.landed = false;
      opt.tween = this.scene.tweens.add({
        targets:  ghost,
        x:        newX,
        y:        newY,
        duration: 160,
        ease:     'Power2.easeOut',
        onComplete: () => this._onOptimisticFlightComplete(opt),
      });
    }
  }

  // ─── Click/tap placement previews ────────────────────────

  /**
   * Show semi-transparent destination previews for a selected hand tile — the
   * actual domino rendered at its exact final placement (same coordinate
   * conversion and pip-order logic as authoritative rendering). Clicking one
   * is resolved via hitTestPlacementPreview from Hand's tap router.
   */
  showPlacementPreviews(tileData: HandTile, vps: ValidPosition[]): void {
    this.clearPlacementPreviews();
    if (!this.scene) return;
    for (const vp of vps) {
      const ed = vp.engine_data as {
        near_val?: number | null; far_val?: number | null; ndir?: string | null;
      } | undefined;
      const { renderA, renderB } = this._resolveRenderValues(
        tileData.value_a, tileData.value_b, vp.orientation, ed);
      const rawLocal = engineToScreen(vp.x, vp.y, this.boardCenterX, this.boardCenterY);
      const local    = this.mobileMode ? rawLocal : { x: Math.round(rawLocal.x), y: Math.round(rawLocal.y) };

      const tile = new DominoTile(
        this.scene, local.x, local.y,
        tileData.id, renderA, renderB, vp.orientation,
        1,
        this.viewerTileSkin !== 'classic' ? getViewerPlacedTextureKey(this.viewerTileSkin, renderA, renderB) : undefined
      );
      // Container child → follows the live board transform (pan/zoom/autoFit).
      this.scene.children.remove(tile);
      this.add(tile);
      tile.setAlpha(0.35);
      tile.highlight();   // existing green "playable" cue doubles as the interactivity hint

      // Desktop hover feedback (cosmetic only — commits go through the tap
      // router). Suppressed while a drag intent state owns the alphas.
      if (!this.mobileMode) {
        tile.on('pointerover', () => { if (this._previewActiveState === null) tile.setAlpha(0.6); });
        tile.on('pointerout',  () => { if (this._previewActiveState === null) tile.setAlpha(0.35); });
      }

      this._placementPreviews.push({ vp, tile });
    }
  }

  /** Remove all destination previews. Safe to call when none exist. */
  clearPlacementPreviews(): void {
    for (const p of this._placementPreviews) p.tile.destroy();
    this._placementPreviews = [];
    this._previewActiveState = null;
  }

  /**
   * Scene-space centers of the current previews — the drag intent-scoring
   * targets. Computed live so they track pan/zoom/autoFit transforms.
   */
  getPlacementPreviewTargets(): Array<{ side: 'left' | 'right'; x: number; y: number }> {
    return this._placementPreviews
      .filter(p => p.tile.active)
      .map(p => ({
        side: p.vp.side,
        x:    this.x + p.tile.x * this.scaleX,
        y:    this.y + p.tile.y * this.scaleY,
      }));
  }

  /**
   * Drive the previews' active-target look during a drag:
   * - a side → that ghost brightens (0.75), the other dims (0.25)
   * - 'rack' → return is the active intent: all ghosts dim (0.25)
   * - null   → back to click-selection idle (0.35, hover re-enabled)
   */
  setActivePlacementPreview(state: 'left' | 'right' | 'rack' | null): void {
    this._previewActiveState = state;
    for (const p of this._placementPreviews) {
      if (!p.tile.active) continue;
      if (state === null)       p.tile.setAlpha(0.35);
      else if (state === 'rack') p.tile.setAlpha(0.25);
      else                       p.tile.setAlpha(p.vp.side === state ? 0.75 : 0.25);
    }
  }

  /**
   * Scene-space hit test against the destination previews, with touch targets
   * enlarged beyond the visible domino footprint (more on mobile). Returns the
   * matched ValidPosition, or null.
   */
  hitTestPlacementPreview(px: number, py: number): ValidPosition | null {
    const padScale = this.mobileMode ? 1.6 : 1.25;
    for (const p of this._placementPreviews) {
      const t = p.tile;
      if (!t.active) continue;
      const wx    = this.x + t.x * this.scaleX;
      const wy    = this.y + t.y * this.scaleY;
      const halfW = (t.width  * this.scaleX * padScale) / 2;
      const halfH = (t.height * this.scaleY * padScale) / 2;
      if (Math.abs(px - wx) <= halfW && Math.abs(py - wy) <= halfH) return p.vp;
    }
    return null;
  }

  /**
   * Authoritative tile for the optimistic tileId was just rendered: hide it
   * and hand it to the ghost. Revealed at touchdown (or immediately if the
   * ghost already landed).
   */
  private _adoptOptimisticTile(tile: DominoTile): void {
    const opt = this._optimistic;
    if (!opt) return;
    tile.setAlpha(0);
    opt.adoptedTile = tile;
    // Completion is deferred to reconcileOptimisticTravel(), which GameScene
    // calls after autoFitToPlayArea — the reveal must always happen under the
    // final post-fit transform, never at a stale pre-fit destination.
  }

  /** Reveal the authoritative tile, destroy the ghost, run the press settle. */
  private _completeOptimisticTouchdown(): void {
    const opt = this._optimistic;
    if (!opt?.adoptedTile) return;
    this._optimistic = null;
    const tile = opt.adoptedTile;
    if (!tile.active || !tile.scene?.tweens) { opt.ghost.destroy(); return; }
    tile.setAlpha(1);          // real tile visible first…
    opt.ghost.destroy();       // …then the ghost goes, same tick — no gap
    tile.animatePlace(this._onTileSettle ?? undefined);
  }

  /**
   * Stop an optimistic flight: rejection, supersede, or teardown.
   * With returnTo, the ghost flies back (same motion as a cancelled drag) and
   * onDone fires after it arrives; without, the ghost is destroyed at once and
   * onDone fires immediately. No-ops (without calling onDone) when no
   * optimistic flight exists, so mobile paths are unaffected.
   */
  cancelOptimisticTravel(returnTo?: { x: number; y: number }, onDone?: () => void): void {
    const opt = this._optimistic;
    if (!opt) return;
    this._optimistic = null;
    if (opt.tween) { opt.tween.remove(); opt.tween = null; }
    const ghost = opt.ghost;
    // Already adopted (confirmed) but force-cancelled (teardown): never leave
    // the authoritative tile hidden.
    if (opt.adoptedTile) {
      if (opt.adoptedTile.active) opt.adoptedTile.setAlpha(1);
      ghost.destroy();
      onDone?.();
      return;
    }
    if (!returnTo || !this.scene?.tweens || !ghost.active) {
      ghost.destroy();
      onDone?.();
      return;
    }
    // Fly home: mirrors _cancelDrag's return motion (250ms Back.Out), rotating
    // back to the hand-matching angle so it visually re-becomes the rack tile.
    this.scene.tweens.killTweensOf(ghost);
    this.scene.tweens.add({
      targets:  ghost,
      x:        returnTo.x,
      y:        returnTo.y,
      scaleX:   1,
      scaleY:   1,
      angle:    opt.startAngle,
      duration: 250,
      ease:     'Back.Out',
      onComplete: () => { ghost.destroy(); onDone?.(); },
    });
  }

  /** Highlight the open ends of the chain */
  highlightEnds(boardEnds: Array<{ side: string; seam: [number, number]; pip: number }>): void {
    // Draw glowing circles at board ends
    for (const end of boardEnds) {
      const pos = engineToScreen(
        end.seam[0],
        end.seam[1],
        this.boardCenterX,
        this.boardCenterY
      );

      const glow = this.scene.add.graphics();
      glow.fillStyle(end.side === 'left' ? 0xff6b6b : 0x4ecdc4, 0.4);
      glow.fillCircle(pos.x, pos.y, getBoardUnitSize() * 0.6);
      glow.lineStyle(2, end.side === 'left' ? 0xff6b6b : 0x4ecdc4, 0.8);
      glow.strokeCircle(pos.x, pos.y, getBoardUnitSize() * 0.6);

      // Add pip number text
      const pipText = this.scene.add.text(pos.x, pos.y, `${end.pip}`, {
        fontSize: `${Math.floor(getBoardUnitSize() * 0.5)}px`,
        color: '#ffffff',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      this.add(glow);
      this.add(pipText);

      // Auto-remove after a few seconds
      this.scene.time.delayedCall(2000, () => {
        glow.destroy();
        pipText.destroy();
      });
    }
  }

  /** Center the view on a specific engine coordinate (with smooth tween) */
  centerOn(engineX: number, engineY: number, animate = true): void {
    if (this.userIsPanning) return;

    const screenCenter = engineToScreen(
      engineX,
      engineY,
      this.boardCenterX,
      this.boardCenterY
    );

    const targetX = this.scene.scale.width / 2 - screenCenter.x * this.currentZoom;
    const targetY = this.scene.scale.height / 2 - screenCenter.y * this.currentZoom;

    if (animate) {
      this.scene.tweens.add({
        targets: this,
        x: targetX,
        y: targetY,
        duration: 300,
        ease: 'Power2',
      });
    } else {
      this.x = targetX;
      this.y = targetY;
    }
  }

  /** Reset pan and zoom to default */
  resetView(): void {
    this.userIsPanning = false;
    this.currentZoom = 1;
    this.setScale(1);
    this.centerOn(0, 0, true);
  }

  /** @deprecated Use GameScene → setBoardUnitSize → fitToZone() instead. */
  handleResize(_width: number, _height: number): void {
    // intentionally empty — GameScene owns layout now
  }

  /**
   * Scale + reposition the board container so the placed tile chain fits
   * inside the given play area.
   *
   * @param cx       Screen X of the play area centre
   * @param cy       Screen Y of the play area centre
   * @param areaW    Width of the play area (px)
   * @param areaH    Height of the play area (px)
   * @param minScale Minimum allowed scale (0 = no minimum). Use > 0 on mobile
   *                 so tiles never become illegibly tiny.
   */
  autoFitToPlayArea(cx: number, cy: number, areaW: number, areaH: number, minScale = 0): void {
    if (this.tileSprites.size === 0) {
      this.setScale(1);
      this.setPosition(cx, cy);
      return;
    }

    const unit = getBoardUnitSize();

    // Bounding box of all tile centres (local container space).
    // Use actual tile half-extents based on orientation:
    //   horizontal → wide (2u) × tall (1u)  →  hw=u,  hh=0.5u
    //   vertical   → wide (1u) × tall (2u)  →  hw=0.5u, hh=u
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    for (const [, tile] of this.tileSprites) {
      const hw = tile.orientation === 'horizontal' ? unit       : unit * 0.5;
      const hh = tile.orientation === 'horizontal' ? unit * 0.5 : unit;
      minX = Math.min(minX, tile.x - hw);
      maxX = Math.max(maxX, tile.x + hw);
      minY = Math.min(minY, tile.y - hh);
      maxY = Math.max(maxY, tile.y + hh);
    }

    const pad = unit * 0.5;
    minX -= pad; maxX += pad;
    minY -= pad; maxY += pad;

    const chainW  = maxX - minX;
    const chainH  = maxY - minY;
    const localCX = (minX + maxX) / 2;
    const localCY = (minY + maxY) / 2;

    // Fit to area — never zoom IN beyond 1× (tiles start large).
    // Clamp to minScale so tiles don't become illegibly tiny on mobile.
    const rawScale    = Math.min(areaW / chainW, areaH / chainH, 1.0);
    const scale       = Math.max(minScale, rawScale);
    this.setScale(scale);

    // Reposition: only when chain actually overflows the area (scale < 1) OR
    // when a minScale is set (mobile always re-centres after every tile).
    if (rawScale < 1.0 || minScale > 0) {
      this.setPosition(cx - localCX * scale, cy - localCY * scale);
    }
  }

  /**
   * Safely stops any in-flight travel tween due to a resize.
   * The ghost is destroyed, the real tile revealed, and the refs cleared.
   * Phaser 3's tween.stop() does NOT call onComplete, so this is clean.
   * Returns the tileId that was snapped, or null if no travel was active.
   */
  snapActiveTravelTween(): number | null {
    if (!this._activeTravelTween) return null;
    const tileId = this._activeTravelTileId;
    this._activeTravelTween.stop();
    this._activeTravelTween = null;
    if (this._activeTravelGhost?.active) this._activeTravelGhost.destroy();
    this._activeTravelGhost = null;
    if (tileId !== null) {
      const tile = this.tileSprites.get(tileId);
      if (tile?.active) tile.setAlpha(1);
    }
    this._activeTravelTileId = null;
    return tileId;
  }

  /**
   * Recompute every existing placed tile's local position from the canonical
   * board state at the current BOARD_UNIT_SIZE. Called immediately after
   * setBoardUnitSize() during resize so sprites reflect the new unit.
   * Returns the number of tiles repositioned.
   */
  reprojectPlacedTiles(tiles: PositionedTile[]): number {
    if (this.tileSprites.size === 0) return 0;
    const byId = new Map<number, PositionedTile>();
    for (const t of tiles) byId.set(t.id, t);
    let count = 0;
    for (const [id, sprite] of this.tileSprites) {
      const td = byId.get(id);
      if (!td) continue;
      const pos = engineToScreen(td.position.x, td.position.y, this.boardCenterX, this.boardCenterY);
      sprite.x = this.mobileMode ? pos.x : Math.round(pos.x);
      sprite.y = this.mobileMode ? pos.y : Math.round(pos.y);
      sprite.redraw();
      count++;
    }
    return count;
  }

  /**
   * Clears stored travel tween refs without stopping any tween.
   * Used during scene cleanup when Phaser destroys everything anyway.
   */
  clearTravelState(): void {
    this._activeTravelTween  = null;
    this._activeTravelGhost  = null;
    this._activeTravelTileId = null;
    this._authTravels.clear();
  }

  /**
   * Convert an engine coordinate to scene-space screen coordinates,
   * accounting for the board container's current position and zoom scale.
   */
  engineToSceneScreen(ex: number, ey: number): { x: number; y: number } {
    const local = engineToScreen(ex, ey, this.boardCenterX, this.boardCenterY);
    return {
      x: this.x + local.x * this.scaleX,
      y: this.y + local.y * this.scaleY,
    };
  }

  /**
   * Draw glowing drop-target circles at each valid board-end position during
   * tile drag. Pass `nearestSide` to brighten the end the pointer is hovering.
   * Graphics are container children so they pan/zoom with the board.
   *
   * Reuses the same Graphics object across calls to avoid allocating a new
   * Phaser object on every pointermove event during a drag.
   */
  showDragHighlights(positions: ValidPosition[], nearestSide: 'left' | 'right' | null): void {
    if (positions.length === 0) { this._clearDragHighlightGfx(); return; }

    // Create the graphics object once; clear + redraw on subsequent calls.
    if (!this._dragHighlightGfx) {
      const gfx = this.scene.add.graphics();
      this._dragHighlightGfx = gfx;
      this.add(gfx);
      gfx.setDepth(180);
    }
    this._dragHighlightGfx.clear();

    const unit = getBoardUnitSize();
    const seen = new Set<'left' | 'right'>();

    for (const vp of positions) {
      if (seen.has(vp.side)) continue;
      seen.add(vp.side);

      const local  = engineToScreen(vp.x, vp.y, this.boardCenterX, this.boardCenterY);
      const isNear = vp.side === nearestSide;
      const color  = vp.side === 'left' ? 0xff6b6b : 0x4ecdc4;
      // Active end: full-opacity ring, solid fill. Idle end: clearly visible but quiet.
      const fillA  = isNear ? 0.60 : 0.28;
      const ringW  = isNear ? 3    : 2;
      const ringA  = isNear ? 1.00 : 0.55;
      const r      = unit * (isNear ? 0.90 : 0.70);

      this._dragHighlightGfx.fillStyle(color, fillA);
      this._dragHighlightGfx.fillCircle(local.x, local.y, r);
      this._dragHighlightGfx.lineStyle(ringW, color, ringA);
      this._dragHighlightGfx.strokeCircle(local.x, local.y, r);
    }
  }

  /** Remove drag drop-target indicators. */
  clearDragHighlights(): void {
    this._clearDragHighlightGfx();
  }

  /** Expose tile sprite map for diagnostics (read-only). */
  getTileSprites(): ReadonlyMap<number, DominoTile> {
    return this.tileSprites;
  }

  /** Transform-aware world bounds of the live placement previews (0–2),
   *  with the side each represents. Read-only geometry accessor (tutorial
   *  spotlight); no gameplay effect. */
  getPlacementPreviewBounds(): Array<{ side: 'left' | 'right'; rect: Phaser.Geom.Rectangle }> {
    return this._placementPreviews.map(p => ({ side: p.vp.side, rect: p.tile.getBounds() }));
  }

  private _clearDragHighlightGfx(): void {
    if (this._dragHighlightGfx) {
      this._dragHighlightGfx.destroy();
      this._dragHighlightGfx = null;
    }
  }
}
